<script setup lang="ts">
import type {EventTimes, DateItem} from '@/types';
import SelectButton from '@/components/forms/SelectButton.vue';
const props = defineProps({
  modelValue: {
    type: Object as PropType<DateItem[]>,
    required: true,
  },
});

const emit = defineEmits(['update:model-value']);

const formModel = ref<DateItem[]>([...props.modelValue]);
const selectedDates = ref<string[]>(initDates());
const currentDateSelect = ref<DateItem | null>(null);
const times = ref<EventTimes[]>(['morning', 'afternoon', 'night']);

const datePicker = ref();
const disableDatePicker = ref(false);

const startDate = ref<Date>(new Date('10/01/2025'));
const maxDate = ref<Date>(new Date('12/31/2026'));
const currentDateRange = ref<Date>(startDate.value);

const validationError = ref(false);

const validDates = computed(() => {
  return formModel.value.length < 1 ? false : true;
});

function initDates() {
  const initDates: string[] = [];
  if (formModel.value.length > 0) {
    formModel.value.forEach((date: DateItem) => {
      initDates.push(date.date);
    });
  }

  return initDates;
}

const showPreviousMonth = computed(() => {
  console.log(startDate.value.getTime() < currentDateRange.value.getTime());
  return startDate.value.getTime() < currentDateRange.value.getTime();
});

function onDateSelected(dates: string[]) {
  if (dates.length > selectedDates.value.length) {
    disableDatePicker.value = true;
    currentDateSelect.value = {
      date: dates[dates.length - 1],
      time: undefined,
    };
  } else {
    const newDates: DateItem[] = [];
    dates.forEach((date: string) => {
      const foundDate = formModel.value.find((index) => new Date(index.date).getTime() === new Date(date).getTime());
      if (foundDate) {
        newDates.push(foundDate);
      }
    });
    formModel.value = newDates;
  }

  selectedDates.value = dates;
}

function onTimeSelected(time: EventTimes) {
  currentDateSelect.value!.time = time;
  formModel.value.push({...currentDateSelect.value!});
  // currentDateSelect.value = null;
  emit('update:model-value', formModel.value);
  disableDatePicker.value = false;
}

function toggleMonth(direction: 'prev' | 'next') {
  if (direction === 'prev') {
    datePicker.value.previousMonth();
  } else {
    datePicker.value.nextMonth();
  }
}

function validate(): boolean {
  if (!props.modelValue) return false;

  if (formModel.value.length < 1) {
    validationError.value = true;
    return false;
  }

  emit('update:model-value', formModel.value);
  return true;
}

defineExpose({validate});
</script>

<template>
  <div class="date-time-container">
    <h3>
      Select a Date & Time
      <span class="small">(Select up to 3 dates)</span>
    </h3>
    <div class="date-time-block">
      <div class="date-time-block-header">
        <button :style="{visibility: showPreviousMonth ? 'visible' : 'hidden'}" @click="toggleMonth('prev')">
          <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 2L3 8L9 14" stroke="white" stroke-width="3" />
          </svg>
        </button>

        <span style="min-width: 140px">{{ currentDateRange.toLocaleString('default', {month: 'long'}) }} {{ currentDateRange.getFullYear() }}</span>

        <button @click="toggleMonth('next')">
          <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 14L8 8L2 2" stroke="white" stroke-width="3" />
          </svg>
        </button>
      </div>

      <div class="date-time-block-content">
        <div class="selector-item" :class="{invalid: validationError && !validDates}">
          <div class="date-picker-wrapper">
            <FormsDatePicker ref="datePicker" :value="selectedDates" :current-date-range="currentDateRange" :disabled="disableDatePicker" :min-date="startDate" :max-date="maxDate" @current-date-range-update="(evt: Date) => (currentDateRange = evt)" @date-selected="onDateSelected" />
          </div>
          <FormsFieldError v-if="!validDates && validationError" style="text-align: center">Please select 1-3 dates</FormsFieldError>

          <Transition name="fade">
            <div v-if="disableDatePicker" class="select-time-prompt">
              <p>Select time of day</p>
              <p class="pointer-arrow">
                <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 14L8 8L2 2" stroke="black" stroke-width="3" />
                </svg>
              </p>
            </div>
          </Transition>
        </div>

        <div class="select-time-day">
          <h4>Time of Day:</h4>

          <ul>
            <li v-for="time in times" :key="time">
              <SelectButton :disabled="!currentDateSelect" @click="onTimeSelected(time)">
                {{ time === 'night' ? 'evening' : time }}
              </SelectButton>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/scss/queries';
.date-time-container {
  flex: 2;
  h3 {
    margin-bottom: 1.5rem;

    .small {
      font-size: 1.25rem;

      @include queries.for-size(phone) {
        font-size: 2rem;
      }
    }
  }

  h4 {
    @include queries.for-size(phone) {
      font-size: 12px !important;
    }
  }

  .date-time-block {
    box-shadow: 0px 4px 17.7px 8px #00000040;
    width: 100%;

    .date-time-block-header {
      align-items: center;
      background-color: black;
      color: white;

      display: flex;
      font-size: 1.6rem;
      font-weight: 700;
      gap: 6rem;
      justify-content: center;
      line-height: 1;
      padding: 1rem 1.5rem;
      text-align: center;

      @include queries.for-size(phone) {
        font-size: 16px !important;
      }

      button {
        background: none;
        background-color: none;
        border: none;
        cursor: pointer;
      }
    }

    .date-time-block-content {
      display: flex;
      gap: 5rem;
      justify-content: space-between;
      padding: 2rem;
      width: 100%;

      @include queries.for-size(phone) {
        flex-direction: column;
      }

      .selector-item {
        position: relative;

        @include queries.for-size(phone) {
          sub {
            display: inline-block;
            font-size: 15px !important;
            margin-top: 1rem;
          }
        }

        .select-time-prompt {
          align-items: center;
          background-color: rgba(255, 255, 255, 0.5);

          display: flex;
          gap: 2rem;
          height: 100%;
          justify-content: flex-end;
          left: 0;
          position: absolute;
          top: 0;
          width: 100%;
          z-index: 10000;

          @include queries.for-size(phone) {
            flex-direction: column;
          }

          p {
            color: black !important;
            font-size: 16px;
            font-weight: 700;
            line-height: 1;
            margin: 0;

            &.pointer-arrow {
              animation: bounce 2s infinite;
              padding-top: 2px;

              @include queries.for-size(phone) {
                rotate: 90deg;
              }

              @keyframes bounce {
                0%,
                20%,
                50%,
                80%,
                100% {
                  transform: translateX(0);
                }
                40% {
                  transform: translateX(-10px);
                }
                60% {
                  transform: translatXY(-5px);
                }
              }
            }
          }
        }
      }

      .select-time-day {
        flex: 1;
      }
    }
  }
}
</style>
